import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Radio, Table } from 'antd';
import moment from 'moment-timezone';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import {
    extractMetricEntries,
    aggregateLatestIntoBuckets,
    getIntervalOptions,
    getDoorDateRange,
} from './doorSensorUtils';

const { Title, Text } = Typography;

/**
 * AiMeterReaderDetail — detail page for the Dragino AIS01-LB AI meter reader.
 *
 * Mirrors DoorSensorDetail's functionality (time-period filters + interval
 * selector + aggregated line chart), but plots the cumulative meter
 * `meter_reading` value (latest reading per bucket) instead of door pulse
 * counts.
 *
 * Unlike the door sensor — which fetches a 30-day summary from the backend —
 * the summary values here are computed client-side from the loaded timelines.
 * Selecting a time-range card refetches that range via onDateRangeChange, so
 * the chart and summary reflect the chosen window. (A dedicated backend
 * summary endpoint could be added later for ranges wider than what's loaded.)
 *
 * Props mirror DoorSensorDetail so SensorDetail can dispatch interchangeably.
 */
const AiMeterReaderDetail = ({
    sensor,
    selectedTimezone,
    isModal = false,
    cardStyle = {},
    dateFormat = 'DD/MM/YYYY HH:mm:ss',
    onDateRangeChange,
    timelineData = [],
    timelineColumns = [],
}) => {
    // Named chartInterval (not `interval`/`setInterval`) to avoid shadowing
    // the global window.setInterval (Q7 variable-shadowing rule).
    const [chartInterval, setChartInterval] = useState('15min');
    const [timeRange, setTimeRange] = useState('today');
    const initializedRef = useRef(false);

    // Sorted cumulative reading entries from the loaded timelines.
    const readingEntries = useMemo(
        () => extractMetricEntries(sensor.timelines, 'meter_reading'),
        [sensor.timelines]
    );

    // Handle time range change from a summary card click.
    const handleTimeRangeChange = useCallback((range) => {
        setTimeRange(range);
        const { start, end, interval: rangeInterval } = getDoorDateRange(range, selectedTimezone);
        setChartInterval(rangeInterval);

        if (onDateRangeChange) {
            onDateRangeChange(start.format(dateFormat), end.format(dateFormat));
        }
    }, [selectedTimezone, dateFormat, onDateRangeChange]);

    // Default to "today" once, on mount.
    useEffect(() => {
        if (selectedTimezone && !initializedRef.current) {
            initializedRef.current = true;
            handleTimeRangeChange('today');
        }
    }, [selectedTimezone, handleTimeRangeChange]);

    // Latest meter reading within a given range (the reading as of the end of
    // that period), computed client-side from the loaded entries.
    const latestReadingForRange = useCallback((range) => {
        const { start, end } = getDoorDateRange(range, selectedTimezone);
        const within = readingEntries.filter(e =>
            e.momentTime.isSameOrAfter(start) && e.momentTime.isSameOrBefore(end)
        );
        if (!within.length) return '-';
        return within[within.length - 1].value;
    }, [readingEntries, selectedTimezone]);

    const summaryValues = useMemo(() => ({
        today: latestReadingForRange('today'),
        yesterday: latestReadingForRange('yesterday'),
        weekly: latestReadingForRange('weekly'),
        daily_average: latestReadingForRange('daily_average'),
    }), [latestReadingForRange]);

    // Latest meter reading per time bucket for the chart.
    const aggregatedData = useMemo(
        () => aggregateLatestIntoBuckets(readingEntries, chartInterval),
        [readingEntries, chartInterval]
    );

    const intervalOptions = useMemo(() => getIntervalOptions(timeRange), [timeRange]);

    // Auto-correct interval if the new range disables the current one.
    useEffect(() => {
        const current = intervalOptions.find(opt => opt.value === chartInterval);
        if (current && current.disabled) {
            const firstEnabled = intervalOptions.find(opt => !opt.disabled);
            if (firstEnabled) setChartInterval(firstEnabled.value);
        }
    }, [intervalOptions, chartInterval]);

    const renderChart = () => {
        if (!aggregatedData.length) {
            return <Text type="secondary">No data available for the selected time range</Text>;
        }

        const timeFormat = chartInterval === 'daily' ? 'DD/MM' :
            chartInterval === '1hour' ? 'DD/MM HH:mm' : 'HH:mm';
        const parseFormat = chartInterval === 'daily' ? 'DD-MM-YYYY' : 'DD-MM-YYYY HH:mm';

        const chartData = aggregatedData.map(d => ({
            ...d,
            displayTime: moment(d.time, parseFormat).format(timeFormat),
        }));

        return (
            <ResponsiveContainer width="100%" height={isModal ? 300 : 400}>
                <LineChart data={chartData} margin={{ left: 20, right: 10, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="displayTime"
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                    />
                    <YAxis
                        label={{ value: 'Reading', angle: -90, position: 'insideLeft', offset: -5 }}
                        domain={['auto', 'auto']}
                    />
                    <Tooltip formatter={(value) => [value, 'Reading']} />
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#0B2A1B"
                        strokeWidth={2}
                        dot={{ r: 3, fill: '#0B2A1B' }}
                        activeDot={{ r: 5 }}
                        name="Reading"
                    />
                </LineChart>
            </ResponsiveContainer>
        );
    };

    const summaryCards = [
        { key: 'today', label: 'Today' },
        { key: 'yesterday', label: 'Yesterday' },
        { key: 'daily_average', label: 'Last 30 Days' },
        { key: 'weekly', label: 'This Week' },
    ];

    const chartTitle = `Reading - ${
        timeRange === 'today' ? 'Today' :
        timeRange === 'yesterday' ? 'Yesterday' :
        timeRange === 'weekly' ? 'This Week' :
        'Last 30 Days'
    } (${
        chartInterval === '15min' ? '15 min' :
        chartInterval === '1hour' ? '1 hour' :
        'Daily'
    })`;

    return (
        <Row gutter={[isModal ? 16 : 24, isModal ? 16 : 24]}>
            {/* Reading Summary Cards (double as the time-range selector) */}
            <Col span={24}>
                <Card title="Summary" style={cardStyle}>
                    <Row gutter={[isModal ? 12 : 16, isModal ? 12 : 16]}>
                        {summaryCards.map(item => (
                            <Col xs={12} sm={12} lg={6} key={item.key}>
                                <Card
                                    size="small"
                                    hoverable
                                    onClick={() => handleTimeRangeChange(item.key)}
                                    style={{
                                        cursor: 'pointer',
                                        border: timeRange === item.key ? '2px solid #0B2A1B' : undefined,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Text strong style={{ fontSize: isModal ? '12px' : '14px' }}>
                                        {item.label}
                                    </Text>
                                    <div style={{ marginTop: '8px' }}>
                                        <Title level={isModal ? 4 : 3} style={{ margin: 0, color: '#0B2A1B' }}>
                                            {summaryValues[item.key]}
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>reading</Text>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card>
            </Col>

            {/* Interval Selector + Usage Chart */}
            <Col span={24}>
                <Card
                    title={chartTitle}
                    extra={
                        <Radio.Group
                            value={chartInterval}
                            onChange={(e) => setChartInterval(e.target.value)}
                            size={isModal ? 'small' : 'middle'}
                        >
                            {intervalOptions.map(opt => (
                                <Radio.Button key={opt.value} value={opt.value} disabled={opt.disabled}>
                                    {opt.label}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    }
                    style={cardStyle}
                >
                    {renderChart()}
                </Card>
            </Col>

            {/* Timeline Table */}
            <Col span={24}>
                <Card title="Recent Data">
                    <Table
                        dataSource={timelineData}
                        columns={timelineColumns}
                        pagination={{
                            pageSize: isModal ? 5 : 10,
                            showSizeChanger: !isModal,
                            showQuickJumper: !isModal,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} readings`,
                        }}
                        scroll={{ x: true }}
                        size="small"
                    />
                </Card>
            </Col>
        </Row>
    );
};

export default AiMeterReaderDetail;
